import { NativeInteropMessageProto } from "@noom/noom-contracts/noom_contracts/mobile/webview/interop";
import type { DeviceDataRequestProto } from "@noom/noom-contracts/noom_contracts/mobile/webview/device_data";
import {
  isInsideAndroidWebView,
  isInsideIOSWebKit,
} from "src/pageDefinitions/goto/utils";
import {
  getAndroidMessageHandlers,
  getIOSMessageHandlers,
} from "src/utils/in-app/messageHandlers";
import {
  getNoomSessionStorage,
  setNoomSessionStorage,
} from "src/utils/noomSessionStorage";

const NATIVE_CALLBACK = "sendJavascriptInteropMessage";
const REQUEST_TIMEOUT_MS = 1500;
const SESSION_STORAGE_KEY = "firebaseAppInstanceId";
const FIREBASE_APP_INSTANCE_ID_KEY = "FIREBASE_APP_INSTANCE_ID";

// Single-flight: avoid multiple concurrent native requests racing / duplicating work.
let inFlight: Promise<string | null> | null = null;

export async function getFirebaseAppInstanceId(): Promise<string | null> {
  const cached = getNoomSessionStorage<string | null>(SESSION_STORAGE_KEY);

  if (typeof cached === "string") {
    return cached;
  }

  if (inFlight) {
    return inFlight;
  }

  inFlight = (async () => {
    const isAndroid = isInsideAndroidWebView();
    const isIOSWebView = isInsideIOSWebKit();

    if (!isAndroid && !isIOSWebView) {
      setNoomSessionStorage(SESSION_STORAGE_KEY, null);
      return null;
    }

    return new Promise<string | null>((resolve) => {
      const messageId = crypto.randomUUID();
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      const originalCallback = (window as any)[NATIVE_CALLBACK];

      const restoreCallback = () => {
        if (originalCallback) {
          (window as any)[NATIVE_CALLBACK] = originalCallback;
        } else {
          delete (window as any)[NATIVE_CALLBACK];
        }
      };

      (window as any)[NATIVE_CALLBACK] = (message: any) => {
        try {
          const response =
            typeof message === "string" ? JSON.parse(message) : message;
          const { respondsTo, deviceDataResponse } = response ?? {};

          if (respondsTo === messageId) {
            if (timeoutId) clearTimeout(timeoutId);
            restoreCallback();

            const value =
              deviceDataResponse?.deviceData?.[FIREBASE_APP_INSTANCE_ID_KEY] ??
              null;

            setNoomSessionStorage(SESSION_STORAGE_KEY, value);
            resolve(value);
            return;
          }
        } catch (err) {
          // ignore; allow unrelated messages to flow through below
        }

        // Forward any unrelated messages to the original callback (if present)
        try {
          if (originalCallback) originalCallback(message);
        } catch {
          // ignore
        }
      };

      timeoutId = setTimeout(() => {
        restoreCallback();
        setNoomSessionStorage(SESSION_STORAGE_KEY, null);
        resolve(null);
      }, REQUEST_TIMEOUT_MS);

      try {
        const deviceDataRequest: DeviceDataRequestProto = {
          requestedDeviceData: [{ value: FIREBASE_APP_INSTANCE_ID_KEY } as any],
        };

        const nativeMessage: NativeInteropMessageProto = {
          messageId,
          deviceDataRequest,
        };

        if (isIOSWebView) {
          const iosHandlers = getIOSMessageHandlers();
          if (iosHandlers?.sendNativeInteropMessage) {
            iosHandlers.sendNativeInteropMessage.postMessage(nativeMessage);
          } else {
            throw new Error("iOS message handler not available");
          }
        } else if (isAndroid) {
          const androidHandlers = getAndroidMessageHandlers();
          if (androidHandlers?.sendNativeInteropMessage) {
            androidHandlers.sendNativeInteropMessage(
              JSON.stringify(nativeMessage)
            );
          } else {
            throw new Error("Android message handler not available");
          }
        } else {
          throw new Error("Unsupported platform");
        }
      } catch (err) {
        if (timeoutId) clearTimeout(timeoutId);
        restoreCallback();
        setNoomSessionStorage(SESSION_STORAGE_KEY, null);
        resolve(null);
      }
    });
  })();

  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}
